/**
 * Client for POST /api/lessons/ai/outline — lesson structure (outline) only.
 * @typedef {Object} LessonAiFilters
 * @property {string} cefrLevel
 * @property {number} durationMinutes
 * @property {string[]} skillSections
 * @property {string[]} exerciseTypes
 * @property {boolean} allowYoutube
 * @property {boolean} allowExternalLinks
 * @property {boolean} authenticSourcesOnly
 */

/**
 * @typedef {Object} LessonOutlineSection
 * @property {string} id
 * @property {string} title
 * @property {string} objective
 * @property {number} estimatedMinutes
 * @property {'main'|'doc'|'homework'} blockType
 * @property {string[]} [suggestedExerciseTypes]
 * @property {string} [htmlContent] Full HTML for the section editor (when present, used instead of the short seed template)
 * @property {Array<{ type?: string, exerciseType?: string, attrs?: object }>} [contentBlocks] TipTap exercise nodes appended after section HTML
 */

/**
 * @typedef {Object} LessonOutlineResponse
 * @property {number} version
 * @property {boolean} mock
 * @property {string} lessonTitle
 * @property {string} cefrLevel
 * @property {number} requestedDurationMinutes
 * @property {Object} filters
 * @property {LessonOutlineSection[]} sections
 * @property {string} generatedAt
 */

/** whiteboard → doc (отдельные доски из outline не используем) */
export function normalizeLessonOutlineResponse(data) {
  if (!data || typeof data !== 'object' || !Array.isArray(data.sections)) return data;
  return {
    ...data,
    sections: data.sections.map((sec) => {
      if (!sec || typeof sec !== 'object') return sec;
      const bt = sec.blockType || 'doc';
      return {
        ...sec,
        blockType: bt === 'whiteboard' ? 'doc' : bt,
      };
    }),
  };
}

/**
 * @param {{ title: string, filters: LessonAiFilters }} payload
 * @returns {Promise<LessonOutlineResponse>}
 */
export async function fetchLessonOutline(payload) {
  const res = await fetch('/api/lessons/ai/outline', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: payload.title || '',
      filters: payload.filters,
      useMock: payload.useMock === true,
      teacherMaterial: payload.teacherMaterial,
      materialUrl: payload.materialUrl,
      materialTypes: payload.materialTypes,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Outline request failed (${res.status})`);
  }
  return normalizeLessonOutlineResponse(data);
}

/**
 * @param {{ lessonTitle: string, cefrLevel: string, sections: object[], instruction: string }} payload
 * @returns {Promise<{ sections: object[] }>}
 */
export async function modifyLessonOutline(payload) {
  const res = await fetch('/api/lessons/ai/modify-outline', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Outline modification failed (${res.status})`);
  return data;
}

/**
 * @param {{ lessonTitle: string, cefrLevel: string, section: object, instruction: string }} payload
 * @returns {Promise<{ section: object }>}
 */
export async function modifyLessonSection(payload) {
  const res = await fetch('/api/lessons/ai/modify-section', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Section modification failed (${res.status})`);
  return data;
}

/**
 * @param {string} title - partial lesson title (debounced on caller)
 * @returns {Promise<{ suggestedSkillSections: string[], suggestedExerciseTypes: string[], hints: string[], source: string }>}
 */
export async function fetchTitleSuggest(title, signal) {
  const res = await fetch('/api/lessons/ai/title-suggest', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title: title || '' }),
    signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Suggest failed (${res.status})`);
  }
  return data;
}
